import '@styles/components/modal.css'
import ajax from '@common/ajax'
import { on, off } from 'delegated-events'
import A11yDialog from 'a11y-dialog'

const generateTemplate = (id, useDefaultClose = true, useCustomClose = false, title = false) => {
  const role = useDefaultClose ? 'dialog' : 'alertdialog'
  const labeledBy = title ? 'aria-labelledby="modal-title"' : ''

  const html = `
    <div id="${id}" aria-hidden="true" class="modal-wrapper" ${labeledBy} role="${role}">
      <div class="modal-overlay" ${useDefaultClose && 'data-a11y-dialog-hide'}></div>

      ${
        useCustomClose === false
          ? '<button type="button" class="modal-close" data-a11y-dialog-hide aria-label="Close dialog"> <svg viewBox="0 0 24 24" aria-hidden="true" class="m-auto w-3/5 h-3/5"> <path fill="currentColor" d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" /> </svg> </button>'
          : ''
      }

      <div class="modal-container">
        <svg class="site-loader site-loader--centered site-loader--light" viewBox="0 0 50 50" aria-hidden="true">
          <circle class="path" cx="25" cy="25" r="20" fill="none" stroke-width="5"></circle>
        </svg>
      </div>
    </div>
  `

  return html
}

const populateModal = async (opts = {}, modalEl) => {
  const type = opts.type
  const title = opts.title ?? ''
  const url = opts.url

  let modalContent = ''

  if (type === 'video') {
    modalContent = `
      <div class="mx-auto w-full max-w-5xl modal-content" role="document">
        <h1 id="modal-title" class="sr-only">Watch ${title}</h1>
        <div class="relative aspect-video modal-body">
          <iframe title="Watch ${title}" style="border: none" src="${url}?autoplay=1&rel=0&enablejsapi=1" allowfullscreen class="absolute inset-0 w-full h-full"></iframe>
        </div>
      </div>
    `

    modalEl.querySelector('.modal-container').innerHTML = modalContent
  }

  if (type === 'content') {
    modalContent = await ajax.get(url).text()

    modalEl.querySelector('.modal-container').innerHTML = modalContent
  }
}

const handleModal = (e) => {
  e.preventDefault()

  const triggerEl = e.target.closest('[data-modal]')

  openModal({
    type: triggerEl.dataset.modal,
    title: triggerEl.dataset.modalTitle,
    url: triggerEl.href,
    updateUrl: triggerEl.dataset.modalUpdateUrl ?? false,
    useDefaultClose: triggerEl.dataset.modalDisableDefaultClose === undefined,
    useCustomClose: triggerEl.dataset.modalUseCustomClose !== undefined,
  })

  return false
}

const openModal = (opts = {}) => {
  const type = opts.type
  const title = opts.title ?? false
  const useCustomClose = opts.useCustomClose ?? false
  let useDefaultClose = opts.useDefaultClose ?? true

  if (type === 'video') {
    useDefaultClose = false
  }

  if (type === 'subscribe') {
    useDefaultClose = false
  }

  const id = `modal-${Math.floor(Math.random() * Date.now())}`
  const template = generateTemplate(id, useDefaultClose, useCustomClose, title)
  const node = document.createRange().createContextualFragment(template)

  document.body.append(node)

  const modalEl = document.getElementById(id)
  const modal = new A11yDialog(modalEl)

  const hideModal = () => {
    modal.hide()
  }

  if (useCustomClose) {
    on('click', '.modal-custom-close', hideModal)
  }

  const currentUrl = opts.defaultUrl ?? window.location.href

  modal.on('show', () => {
    document.documentElement.classList.add('has-modal-active')

    if (opts.updateUrl !== false) {
      window.history.replaceState({}, '', opts.updateUrl)
    }
  })

  modal.on('hide', () => {
    document.documentElement.classList.remove('has-modal-active')

    modal.destroy()

    if (opts.updateUrl !== false) {
      window.history.replaceState({}, '', currentUrl)
    }

    if (useCustomClose) {
      off('click', '.modal-custom-close', hideModal)
    }
  })

  modal.on('destroy', (el) => {
    el.remove()
  })

  modal.show()

  populateModal(
    {
      type: type,
      url: opts.url ?? false,
      updateUrl: opts.updateUrl,
      defaultUrl: opts.defaultUrl,
    },
    modalEl
  )
}

export default {
  init: () => {
    const clickArgs = ['click', '[data-modal]', handleModal]
    off(...clickArgs)
    on(...clickArgs)

    // If we have video modals, load the YouTube API
    if (document.querySelectorAll('[data-modal="video"]').length) {
      const tag = document.createElement('script')
      tag.src = 'https://www.youtube.com/iframe_api'

      const firstScriptTag = document.getElementsByTagName('script')[0]

      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag)
    }
  },
  openModal,
}
